/**
 * Autostaker Module
 * Based on the official Streamr Autostaker Plugin algorithm
 * Allows operators to automatically manage stakes across sponsorships
 */

import { runQuery, switchToFallbackRpc, getCurrentRpcUrl, readWithFallback, getReadOnlyProvider, isRateLimitError as isRateLimitErrorService } from '../core/services.js';
import { convertWeiToData, formatBigNumber, getFriendlyErrorMessage, contractErrorMessage } from '../core/utils.js';
import { OPERATOR_CONTRACT_ABI, SPONSORSHIP_ABI, OPERATOR_V4 } from '../core/constants.js';
import { ethers } from 'ethers';

// ethers is loaded globally from libs/ethers.umd.min.js

// Configuration Constants
const MIN_SPONSORSHIP_TOTAL_PAYOUT_PER_SECOND = BigInt('1000000000000'); // 1e12 wei
const MIN_SPONSORSHIP_BALANCE_DATA = 1; // Minimum balance in DATA tokens
const MIN_SPONSORSHIP_BALANCE_WEI = BigInt(MIN_SPONSORSHIP_BALANCE_DATA) * BigInt('1000000000000000000'); // Convert to wei
const DEFAULT_MAX_SPONSORSHIP_COUNT = 20;
const DEFAULT_MIN_TRANSACTION_AMOUNT = 100; // DATA tokens
const DEFAULT_MAX_ACCEPTABLE_MIN_OPERATOR_COUNT = 4;
const DEFAULT_AUTO_COLLECT_INTERVAL_HOURS = 24; // Auto collect every 24 hours by default


// Storage keys
const AUTOSTAKER_CONFIG_KEY = 'autostaker_config';
const EXCLUDED_SPONSORSHIPS_KEY = 'autostaker_excluded_sponsorships';

/**
 * Get default autostaker configuration
 */
function getDefaultConfig() {
    return {
        maxSponsorshipCount: DEFAULT_MAX_SPONSORSHIP_COUNT,
        minTransactionAmount: DEFAULT_MIN_TRANSACTION_AMOUNT,
        maxAcceptableMinOperatorCount: DEFAULT_MAX_ACCEPTABLE_MIN_OPERATOR_COUNT,
        enabled: false,
        autoCollectEnabled: true,
        autoCollectIntervalHours: DEFAULT_AUTO_COLLECT_INTERVAL_HOURS,
        lastCollectTime: null, // ISO string of last collect time
        ignoreFirstCollect: true
    };
}

/**
 * Load autostaker configuration from localStorage
 * @param {string} operatorId - The operator contract address
 * @returns {Object} Configuration object
 */
export function loadAutostakerConfig(operatorId) {
    try {
        const stored = localStorage.getItem(`${AUTOSTAKER_CONFIG_KEY}_${operatorId.toLowerCase()}`);
        if (stored) {
            return { ...getDefaultConfig(), ...JSON.parse(stored) };
        }
    } catch (e) {
        console.error('Failed to load autostaker config:', e);
    }
    return getDefaultConfig();
}

/**
 * Save autostaker configuration to localStorage
 * @param {string} operatorId - The operator contract address
 * @param {Object} config - Configuration to save
 */
export function saveAutostakerConfig(operatorId, config) {
    try {
        localStorage.setItem(
            `${AUTOSTAKER_CONFIG_KEY}_${operatorId.toLowerCase()}`,
            JSON.stringify(config)
        );
    } catch (e) {
        console.error('Failed to save autostaker config:', e);
    }
}

/**
 * Load excluded sponsorships from localStorage
 * @param {string} operatorId - The operator contract address
 * @returns {Set<string>} Set of excluded sponsorship IDs
 */
export function loadExcludedSponsorships(operatorId) {
    try {
        const stored = localStorage.getItem(`${EXCLUDED_SPONSORSHIPS_KEY}_${operatorId.toLowerCase()}`);
        if (stored) {
            return new Set(JSON.parse(stored));
        }
    } catch (e) {
        console.error('Failed to load excluded sponsorships:', e);
    }
    return new Set();
}

/**
 * Save excluded sponsorships to localStorage
 * @param {string} operatorId - The operator contract address
 * @param {Set<string>} excluded - Set of excluded sponsorship IDs
 */
export function saveExcludedSponsorships(operatorId, excluded) {
    try {
        localStorage.setItem(
            `${EXCLUDED_SPONSORSHIPS_KEY}_${operatorId.toLowerCase()}`,
            JSON.stringify([...excluded])
        );
    } catch (e) {
        console.error('Failed to save excluded sponsorships:', e);
    }
}

/**
 * Fetch minimum stake per sponsorship from The Graph
 * @returns {Promise<bigint>} Minimum stake in wei
 */
async function fetchMinStakePerSponsorship() {
    const query = `
        {
            network(id: "network-entity-id") {
                minimumStakeWei
            }
        }
    `;
    const data = await runQuery(query);
    return BigInt(data.network?.minimumStakeWei || '0');
}

/**
 * Fetch stakeable sponsorships from The Graph
 * @param {Map<string, bigint>} currentStakes - Current stakes by sponsorship ID
 * @param {number} maxAcceptableMinOperatorCount - Max acceptable min operator count
 * @param {Set<string>} excludedSponsorships - Set of excluded sponsorship IDs
 * @returns {Promise<Map<string, Object>>} Map of sponsorship ID to config
 */
/**
 * The full sponsorships among the ones with a max operator count: isFull() on the chain for sponsorships
 * deployed from 2026-10-12, the subgraph's operatorCount / maxOperators for older ones (they have no isFull)
 * @param {Array<{id: string, operatorCount: number|string, maxOperators: string|null}>} sponsorships
 * @returns {Promise<Set<string>>} ids of the full ones
 */
async function fetchFullSponsorships(sponsorships) {
    const full = new Set();
    await Promise.all(sponsorships.filter(sp => sp.maxOperators !== null && sp.maxOperators !== undefined).map(async sp => {
        let isFull;
        try {
            isFull = await readWithFallback(() => new ethers.Contract(sp.id, SPONSORSHIP_ABI, getReadOnlyProvider()).isFull());
        } catch {
            isFull = Number(sp.operatorCount) >= Number(sp.maxOperators);
        }
        if (isFull) full.add(sp.id);
    }));
    return full;
}

async function fetchStakeableSponsorships(currentStakes, maxAcceptableMinOperatorCount, excludedSponsorships) {
    const now = Math.floor(Date.now() / 1000);
    const query = `
        {
            sponsorships(
                where: {
                    projectedInsolvency_gt: ${now}
                    minimumStakingPeriodSeconds: "0"
                    minOperators_lte: ${maxAcceptableMinOperatorCount}
                    totalPayoutWeiPerSec_gte: "${MIN_SPONSORSHIP_TOTAL_PAYOUT_PER_SECOND.toString()}"
                    remainingWei_gte: "${MIN_SPONSORSHIP_BALANCE_WEI.toString()}"
                }
                first: 1000
                orderBy: totalPayoutWeiPerSec
                orderDirection: desc
            ) {
                id
                totalPayoutWeiPerSec
                operatorCount
                maxOperators
                remainingWei
                stream { id }
            }
        }
    `;
    
    const data = await runQuery(query);
    const sponsorships = data.sponsorships || [];
    const full = await fetchFullSponsorships(sponsorships.filter(sp => !currentStakes.has(sp.id)));
    
    // Already staked: kept in the list; else not full
    const hasAcceptableOperatorCount = (item) => currentStakes.has(item.id) || !full.has(item.id);
    
    const result = new Map();
    for (const sp of sponsorships) {
        // Skip excluded sponsorships (unless already staked)
        if (excludedSponsorships.has(sp.id.toLowerCase()) && !currentStakes.has(sp.id)) {
            continue;
        }
        
        if (hasAcceptableOperatorCount(sp)) {
            result.set(sp.id, {
                payoutPerSec: BigInt(sp.totalPayoutWeiPerSec),
                streamId: sp.stream?.id || sp.id,
                operatorCount: sp.operatorCount,
                maxOperators: sp.maxOperators
            });
        }
    }
    
    return result;
}

/**
 * Fetch operator's free balance from The Graph
 * @param {string} operatorId - The operator contract address
 * @returns {Promise<Object>} Object with totalValue, stakedAmount, freeBalance
 */
async function fetchOperatorFreeBalance(operatorId) {
    const query = `
        {
            operator(id: "${operatorId.toLowerCase()}") {
                id
                valueWithoutEarnings
                stakes {
                    amountWei
                }
            }
        }
    `;
    
    try {
        const data = await runQuery(query);
        const operator = data.operator;
        
        if (!operator) {
            return { totalValue: BigInt(0), stakedAmount: BigInt(0), freeBalance: BigInt(0) };
        }
        
        const totalValue = BigInt(operator.valueWithoutEarnings || '0');
        const stakedAmount = (operator.stakes || []).reduce(
            (sum, stake) => sum + BigInt(stake.amountWei || '0'),
            BigInt(0)
        );
        const freeBalance = totalValue > stakedAmount ? totalValue - stakedAmount : BigInt(0);
        
        return { totalValue, stakedAmount, freeBalance };
    } catch (e) {
        console.error('Failed to fetch operator free balance:', e);
        return { totalValue: BigInt(0), stakedAmount: BigInt(0), freeBalance: BigInt(0) };
    }
}

/**
 * Fetch current stakes for an operator
 * @param {string} operatorId - The operator contract address
 * @returns {Promise<Map<string, bigint>>} Map of sponsorship ID to stake amount
 */
async function fetchCurrentStakes(operatorId) {
    const query = `
        {
            stakes(
                where: { operator: "${operatorId.toLowerCase()}" }
                first: 1000
            ) {
                id
                sponsorship { id }
                amountWei
            }
        }
    `;
    
    const data = await runQuery(query);
    const stakes = data.stakes || [];
    
    return new Map(stakes.map(stake => [stake.sponsorship.id, BigInt(stake.amountWei)]));
}

/**
 * Fetch undelegation queue amount for an operator
 * @param {string} operatorId - The operator contract address
 * @returns {Promise<bigint>} Total amount in undelegation queue
 */
async function fetchUndelegationQueueAmount(operatorId) {
    const query = `
        {
            queueEntries(
                where: { operator: "${operatorId.toLowerCase()}" }
                first: 1000
            ) {
                id
                amount
            }
        }
    `;
    
    const data = await runQuery(query);
    const entries = data.queueEntries || [];
    
    return entries.reduce((sum, entry) => sum + BigInt(entry.amount), BigInt(0));
}

/**
 * Fetch all available sponsorships for display (including excluded status)
 * Shows all sponsorships with balance > 0 and no lock period
 * @param {string} operatorId - The operator contract address
 * @returns {Promise<Array>} Array of sponsorship objects with stake info
 */
export async function fetchAllSponsorshipsForDisplay(operatorId) {
    const currentStakes = await fetchCurrentStakes(operatorId);
    const excludedSponsorships = loadExcludedSponsorships(operatorId);
    
    const query = `
        {
            sponsorships(
                where: {
                    remainingWei_gt: "0"
                    minimumStakingPeriodSeconds: "0"
                }
                first: 1000
                orderBy: totalPayoutWeiPerSec
                orderDirection: desc
            ) {
                id
                totalPayoutWeiPerSec
                operatorCount
                maxOperators
                minOperators
                spotAPY
                remainingWei
                projectedInsolvency
                isRunning
                stream { id }
            }
        }
    `;
    
    const data = await runQuery(query);
    const sponsorships = data.sponsorships || [];
    const now = Math.floor(Date.now() / 1000);
    const full = await fetchFullSponsorships(sponsorships);
    
    return sponsorships.map(sp => {
        // Determine why a sponsorship might not be stakeable by autostaker
        const issues = [];
        const info = []; // Informational messages (not blocking)
        const projectedInsolvency = parseInt(sp.projectedInsolvency || '0');
        const remainingWei = BigInt(sp.remainingWei || '0');
        const payoutPerSec = BigInt(sp.totalPayoutWeiPerSec || '0');
        const minOperators = parseInt(sp.minOperators || '0');
        const operatorCount = parseInt(sp.operatorCount || '0');
        
        // Check if sponsorship is not running but CAN be activated by staking
        // A sponsorship becomes "running" when it has >= minOperators staked
        const canBeActivated = !sp.isRunning && operatorCount < minOperators && projectedInsolvency > now;
        
        if (!sp.isRunning && !canBeActivated) {
            // Truly not running and cannot be activated
            issues.push('Not running');
        } else if (canBeActivated) {
            // Not running but can be activated - show as info, not blocking issue
            const needsMore = minOperators - operatorCount;
            info.push(`Needs ${needsMore} operator${needsMore > 1 ? 's' : ''} to activate`);
        }
        
        if (projectedInsolvency <= now) issues.push('Expired');
        if (payoutPerSec < MIN_SPONSORSHIP_TOTAL_PAYOUT_PER_SECOND) issues.push('Low payout');
        if (remainingWei < MIN_SPONSORSHIP_BALANCE_WEI) issues.push('Low balance');
        if (minOperators > DEFAULT_MAX_ACCEPTABLE_MIN_OPERATOR_COUNT) issues.push(`Min operators: ${minOperators}`);
        if (full.has(sp.id)) issues.push('Full');
        
        return {
            id: sp.id,
            streamId: sp.stream?.id || sp.id,
            payoutPerSec: sp.totalPayoutWeiPerSec,
            operatorCount: sp.operatorCount,
            maxOperators: sp.maxOperators,
            minOperators: sp.minOperators,
            spotAPY: sp.spotAPY,
            remainingWei: sp.remainingWei,
            projectedInsolvency: sp.projectedInsolvency,
            isRunning: sp.isRunning,
            canBeActivated: canBeActivated,
            currentStake: currentStakes.get(sp.id) || BigInt(0),
            isStaked: currentStakes.has(sp.id),
            isExcluded: excludedSponsorships.has(sp.id.toLowerCase()),
            isStakeable: issues.length === 0,
            issues: issues,
            info: info
        };
    });
}

/**
 * Sum of bigints
 * @param {bigint[]} values - Array of bigints
 * @returns {bigint} Sum
 */
function sumBigInts(values) {
    return values.reduce((sum, val) => sum + val, BigInt(0));
}

/**
 * BigInt max helper
 */
function bigIntMax(...args) {
    return args.reduce((m, e) => e > m ? e : m);
}

/**
 * BigInt min helper
 */
function bigIntMin(...args) {
    return args.reduce((m, e) => e < m ? e : m);
}

/**
 * BigInt abs helper
 */
function bigIntAbs(n) {
    return n < BigInt(0) ? -n : n;
}

/**
 * Simple hash function for deterministic tie-breaking (like MD5 in official)
 * Uses FNV-1a algorithm for simplicity in browser
 */
function hashString(str) {
    let hash = 2166136261;
    for (let i = 0; i < str.length; i++) {
        hash ^= str.charCodeAt(i);
        hash = (hash * 16777619) >>> 0;
    }
    return hash;
}

/**
 * Get expired sponsorships (currently staked but no longer in stakeable list)
 */
function getExpiredSponsorships(myCurrentStakes, stakeableSponsorships) {
    return [...myCurrentStakes.keys()].filter(id => !stakeableSponsorships.has(id));
}

/**
 * Select sponsorships to stake 
 * Selects the most profitable sponsorships up to maxSponsorshipCount
 * When maxSponsorshipCount is reduced, less profitable sponsorships will be abandoned
 */
function getSelectedSponsorships(
    myCurrentStakes,
    stakeableSponsorships,
    totalStakeableAmount,
    operatorContractAddress,
    maxSponsorshipCount,
    minStakePerSponsorship
) {
    // Calculate how many we can afford
    const count = Math.min(
        stakeableSponsorships.size,
        maxSponsorshipCount,
        minStakePerSponsorship > BigInt(0) 
            ? Number(totalStakeableAmount / minStakePerSponsorship) 
            : Infinity
    );
    
    if (count <= 0) return [];
    
    // Get all stakeable sponsorships and sort by profitability
    const allSponsorships = [...stakeableSponsorships.keys()];
    
    // Sort ALL sponsorships by:
    // 1. payoutPerSec (descending) - most profitable first
    // 2. Hash of operator+sponsorship (for deterministic tie-breaking)
    const sortedSponsorships = allSponsorships.sort((a, b) => {
        const payoutA = stakeableSponsorships.get(a).payoutPerSec;
        const payoutB = stakeableSponsorships.get(b).payoutPerSec;
        
        if (payoutB > payoutA) return 1;
        if (payoutA > payoutB) return -1;
        
        // Tie-breaker: hash of operatorAddress + sponsorshipId
        const hashA = hashString(operatorContractAddress + a);
        const hashB = hashString(operatorContractAddress + b);
        return hashA - hashB;
    });
    
    // Select the top 'count' most profitable sponsorships
    // This ensures that when maxSponsorshipCount is reduced,
    // the least profitable sponsorships are dropped, even if currently staked
    return sortedSponsorships.slice(0, count);
}

/**
 * Calculate target stakes for each sponsorship - OFFICIAL ALGORITHM
 * Formula: target = minStake + (payoutProportionalAmount * payoutPerSec / totalPayoutPerSec)
 */
function getTargetStakes(
    myCurrentStakes,
    myUnstakedAmount,
    stakeableSponsorships,
    undelegationQueueAmount,
    operatorContractAddress,
    maxSponsorshipCount,
    minStakePerSponsorship
) {
    const totalStakeableAmount = sumBigInts([...myCurrentStakes.values()]) + myUnstakedAmount - undelegationQueueAmount;
    
    const selectedSponsorships = getSelectedSponsorships(
        myCurrentStakes,
        stakeableSponsorships,
        totalStakeableAmount,
        operatorContractAddress,
        maxSponsorshipCount,
        minStakePerSponsorship
    );
    
    if (selectedSponsorships.length === 0) {
        // Return targets for expired sponsorships (unstake all)
        const expiredTargets = new Map();
        for (const id of getExpiredSponsorships(myCurrentStakes, stakeableSponsorships)) {
            expiredTargets.set(id, BigInt(0));
        }
        return expiredTargets;
    }
    
    // Calculate payout-proportional allocation
    const minStakePerSponsorshipSum = BigInt(selectedSponsorships.length) * minStakePerSponsorship;
    const payoutProportionalAmount = totalStakeableAmount > minStakePerSponsorshipSum 
        ? totalStakeableAmount - minStakePerSponsorshipSum 
        : BigInt(0);
    
    const payoutPerSecSum = sumBigInts(selectedSponsorships.map(id => stakeableSponsorships.get(id).payoutPerSec));
    
    const targetStakes = new Map();
    
    // Target for selected sponsorships
    for (const id of selectedSponsorships) {
        const proportionalPart = payoutPerSecSum > BigInt(0)
            ? (payoutProportionalAmount * stakeableSponsorships.get(id).payoutPerSec) / payoutPerSecSum
            : BigInt(0);
        targetStakes.set(id, minStakePerSponsorship + proportionalPart);
    }
    
    // Target 0 for sponsorships we're currently staked in but not selected
    for (const id of myCurrentStakes.keys()) {
        if (!selectedSponsorships.includes(id)) {
            targetStakes.set(id, BigInt(0));
        }
    }
    
    // Target 0 for expired sponsorships
    for (const id of getExpiredSponsorships(myCurrentStakes, stakeableSponsorships)) {
        targetStakes.set(id, BigInt(0));
    }
    
    return targetStakes;
}

/**
 * Payout Proportional Strategy - Calculate stake adjustments
 * Based EXACTLY on the official AutostakerPlugin algorithm
 * @param {Object} params - Strategy parameters
 * @returns {Array} Array of actions to execute
 */
function adjustStakes({
    myCurrentStakes,
    myUnstakedAmount,
    stakeableSponsorships,
    undelegationQueueAmount,
    operatorContractAddress,
    maxSponsorshipCount,
    minTransactionAmount,
    minStakePerSponsorship
}) {
    const targetStakes = getTargetStakes(
        myCurrentStakes,
        myUnstakedAmount,
        stakeableSponsorships,
        undelegationQueueAmount,
        operatorContractAddress,
        maxSponsorshipCount,
        minStakePerSponsorship
    );
    
    // Calculate adjustments (difference between target and current)
    let adjustments = [...targetStakes.keys()]
        .map(sponsorshipId => {
            const target = targetStakes.get(sponsorshipId) || BigInt(0);
            const current = myCurrentStakes.get(sponsorshipId) || BigInt(0);
            const difference = target - current;
            
            console.log(`[Autostaker] Sponsorship ${sponsorshipId.substring(0, 10)}... : current=${current}, target=${target}, diff=${difference}`);
            
            return {
                sponsorshipId,
                difference,
                currentStake: current,
                targetStake: target
            };
        })
        .filter(({ difference }) => difference !== BigInt(0));
    
    // Filter out too-small adjustments (except for expired sponsorships which must be unstaked)
    const tooSmallAdjustments = adjustments.filter(
        a => bigIntAbs(a.difference) < minTransactionAmount && stakeableSponsorships.has(a.sponsorshipId)
    );
    adjustments = adjustments.filter(a => !tooSmallAdjustments.includes(a));
    
    // Balance out any excess staking from filtering small adjustments
    while (true) {
        const stakings = adjustments.filter(a => a.difference > BigInt(0));
        const unstakings = adjustments.filter(a => a.difference < BigInt(0));
        const stakingSum = sumBigInts(stakings.map(a => a.difference));
        const availableSum = bigIntAbs(sumBigInts(unstakings.map(a => a.difference))) + myUnstakedAmount - undelegationQueueAmount;
        let excess = stakingSum - availableSum;
        
        if (excess > BigInt(0) && stakings.length > 0) {
            // Calculate allowances for reducing stakings
            const stakingReductionAllowances = new Map(
                stakings.map(a => {
                    const minDiff = bigIntMax(
                        minTransactionAmount,
                        myCurrentStakes.has(a.sponsorshipId) ? BigInt(0) : minStakePerSponsorship
                    );
                    const allowance = bigIntMax(a.difference - minDiff, BigInt(0));
                    return [a.sponsorshipId, allowance];
                })
            );
            const totalAllowance = sumBigInts([...stakingReductionAllowances.values()]);
            
            if (excess > totalAllowance) {
                // Remove smallest staking
                const smallestStaking = stakings.reduce((min, a) => 
                    a.difference < min.difference ? a : min, stakings[0]);
                adjustments = adjustments.filter(a => a !== smallestStaking);
            } else {
                // Reduce stakings proportionally
                for (const staking of stakings) {
                    const allowance = stakingReductionAllowances.get(staking.sponsorshipId);
                    if (allowance > BigInt(0)) {
                        const reduction = bigIntMin(allowance, excess);
                        excess -= reduction;
                        staking.difference -= reduction;
                        if (excess <= BigInt(0)) break;
                    }
                }
                break;
            }
        } else {
            break;
        }
    }
    
    // Convert to actions and sort (unstakes first)
    const actions = adjustments
        .map(({ sponsorshipId, difference, currentStake, targetStake }) => {
            // For unstake, ensure targetStake is not greater than currentStake
            // This prevents arithmetic underflow in the contract
            const safeTargetStake = difference < BigInt(0) 
                ? (targetStake > currentStake ? BigInt(0) : targetStake)  // If target > current, set to 0 (full unstake)
                : targetStake;
            
            const isUnstake = difference < BigInt(0);
            
            // For unstake, recalculate amount based on safe target
            const safeAmount = isUnstake 
                ? (currentStake - safeTargetStake)  // How much we're actually removing
                : bigIntAbs(difference);
            
            console.log(`[Autostaker] Action: ${isUnstake ? 'unstake' : 'stake'} ${sponsorshipId.substring(0, 10)}... amount=${safeAmount}, targetStake=${safeTargetStake}`);
            
            return {
                type: isUnstake ? 'unstake' : 'stake',
                sponsorshipId,
                amount: safeAmount,
                targetStake: safeTargetStake,
                currentStake
            };
        })
        .filter(action => {
            // Skip actions with 0 amount
            if (action.amount === BigInt(0)) {
                console.warn(`[Autostaker] Skipping zero-amount action for ${action.sponsorshipId}`);
                return false;
            }
            // Additional safety: skip unstake actions where target >= current (nothing to unstake)
            if (action.type === 'unstake' && action.targetStake >= action.currentStake) {
                console.warn(`[Autostaker] Skipping invalid unstake: target (${action.targetStake}) >= current (${action.currentStake}) for ${action.sponsorshipId}`);
                return false;
            }
            return true;
        })
        .sort((a, b) => {
            const typeOrder = ['unstake', 'stake'];
            return typeOrder.indexOf(a.type) - typeOrder.indexOf(b.type);
        });
    
    return actions;
}

/**
 * Analyze current state and calculate recommended actions
 * @param {string} operatorId - The operator contract address
 * @param {Object} config - Autostaker configuration
 * @param {ethers.Contract} operatorContract - Operator contract instance
 * @returns {Promise<Object>} Analysis result with actions
 */
export async function analyzeAndCalculateActions(operatorId, config, operatorContract) {
    const excludedSponsorships = loadExcludedSponsorships(operatorId);
    
    // Fetch all required data
    const [minStakePerSponsorship, myCurrentStakes, undelegationQueueAmount, operatorData] = await Promise.all([
        fetchMinStakePerSponsorship(),
        fetchCurrentStakes(operatorId),
        fetchUndelegationQueueAmount(operatorId),
        fetchOperatorFreeBalance(operatorId)
    ]);
    
    // Get stakeable sponsorships (excluding user's exclusion list)
    const stakeableSponsorships = await fetchStakeableSponsorships(
        myCurrentStakes,
        config.maxAcceptableMinOperatorCount,
        excludedSponsorships
    );
    
    // Get operator's free funds from The Graph data
    let myUnstakedAmount = operatorData.freeBalance;
    
    // Fallback to contract if Graph data not available
    if (myUnstakedAmount === BigInt(0)) {
        try {
            const valueWithoutEarnings = await readWithFallback(() => operatorContract.valueWithoutEarnings());
            const myStakedAmount = sumBigInts([...myCurrentStakes.values()]);
            myUnstakedAmount = valueWithoutEarnings.gt(myStakedAmount.toString()) 
                ? BigInt(valueWithoutEarnings.toString()) - myStakedAmount 
                : BigInt(0);
            console.log(`[Autostaker] Fallback: valueWithoutEarnings=${valueWithoutEarnings.toString()}, staked=${myStakedAmount.toString()}, free=${myUnstakedAmount.toString()}`);
        } catch (e) {
            console.warn('Failed to get operator free funds from contract:', e.message);
        }
    }
    
    // Log key values for debugging
    const totalCurrentStakes = sumBigInts([...myCurrentStakes.values()]);
    const currentSponsorshipCount = myCurrentStakes.size;
    
    console.log('[Autostaker] Analysis:', {
        currentStakesTotal: convertWeiToData(totalCurrentStakes.toString()) + ' DATA',
        currentSponsorshipCount: currentSponsorshipCount,
        maxSponsorshipCount: config.maxSponsorshipCount,
        freeBalance: convertWeiToData(myUnstakedAmount.toString()) + ' DATA',
        undelegationQueue: convertWeiToData(undelegationQueueAmount.toString()) + ' DATA',
        minStakePerSponsorship: convertWeiToData(minStakePerSponsorship.toString()) + ' DATA',
        stakeableSponsorshipsCount: stakeableSponsorships.size
    });
    
    // Log if we need to reduce sponsorship count
    if (currentSponsorshipCount > config.maxSponsorshipCount) {
        const toReduce = currentSponsorshipCount - config.maxSponsorshipCount;
        console.log(`[Autostaker] Need to reduce sponsorship count by ${toReduce} (current: ${currentSponsorshipCount}, max: ${config.maxSponsorshipCount})`);
        console.log('[Autostaker] Will unstake from least profitable sponsorships to match the new limit');
    }
    
    // Convert minTransactionAmount to wei
    const minTransactionAmountWei = BigInt(config.minTransactionAmount) * BigInt('1000000000000000000');
    
    // Check if there's a pending undelegation queue that needs to be paid
    // If freeBalance is insufficient, we need to unstake to cover the queue
    const hasUnpaidQueue = undelegationQueueAmount > BigInt(0) && myUnstakedAmount < undelegationQueueAmount;
    const amountNeededForQueue = hasUnpaidQueue ? (undelegationQueueAmount - myUnstakedAmount) : BigInt(0);
    
    if (hasUnpaidQueue) {
        console.log('[Autostaker] Detected unpaid undelegation queue with insufficient free balance');
        console.log(`[Autostaker] Queue: ${convertWeiToData(undelegationQueueAmount.toString())} DATA, Free: ${convertWeiToData(myUnstakedAmount.toString())} DATA`);
        console.log(`[Autostaker] Need to unstake: ${convertWeiToData(amountNeededForQueue.toString())} DATA to pay queue`);
        
        // Generate an action to unstake enough to pay the queue
        // Find the best sponsorship to unstake from (one with highest stake above minimum)
        const queuePaymentAction = generateQueuePaymentAction(
            myCurrentStakes,
            stakeableSponsorships,
            amountNeededForQueue,
            minStakePerSponsorship
        );
        
        if (queuePaymentAction) {
            console.log(`[Autostaker] Generated queue payment action: unstake ${convertWeiToData(queuePaymentAction.amount.toString())} DATA from ${queuePaymentAction.sponsorshipId.substring(0, 10)}...`);
            
            return {
                actions: [queuePaymentAction],
                currentStakes: myCurrentStakes,
                stakeableSponsorships,
                myUnstakedAmount,
                undelegationQueueAmount,
                minStakePerSponsorship,
                excludedCount: excludedSponsorships.size,
                isQueuePayment: true,
                queuePaymentAmount: amountNeededForQueue
            };
        } else {
            console.warn('[Autostaker] Cannot generate queue payment action - no sponsorship with enough stake above minimum');
            return {
                actions: [],
                currentStakes: myCurrentStakes,
                stakeableSponsorships,
                myUnstakedAmount,
                undelegationQueueAmount,
                minStakePerSponsorship,
                excludedCount: excludedSponsorships.size,
                skippedReason: 'Cannot pay undelegation queue - no sponsorship has enough stake above minimum to unstake'
            };
        }
    }
    
    // Calculate actions
    const actions = adjustStakes({
        myCurrentStakes,
        myUnstakedAmount,
        stakeableSponsorships,
        undelegationQueueAmount,
        operatorContractAddress: operatorId,
        maxSponsorshipCount: config.maxSponsorshipCount,
        minTransactionAmount: minTransactionAmountWei,
        minStakePerSponsorship
    });
    
    return {
        actions,
        currentStakes: myCurrentStakes,
        stakeableSponsorships,
        myUnstakedAmount,
        undelegationQueueAmount,
        minStakePerSponsorship,
        excludedCount: excludedSponsorships.size
    };
}

/**
 * Generate an action to unstake enough to pay the undelegation queue
 * @param {Map<string, bigint>} currentStakes - Current stakes by sponsorship ID
 * @param {Map<string, Object>} stakeableSponsorships - Stakeable sponsorships info
 * @param {bigint} amountNeeded - Amount needed to pay the queue
 * @param {bigint} minStakePerSponsorship - Minimum stake per sponsorship
 * @returns {Object|null} Unstake action or null if not possible
 */
function generateQueuePaymentAction(currentStakes, stakeableSponsorships, amountNeeded, minStakePerSponsorship) {
    // Find sponsorships we can unstake from (stake > minStake)
    const unstakeableSponsorships = [...currentStakes.entries()]
        .filter(([id, stake]) => stake > minStakePerSponsorship)
        .map(([id, stake]) => ({
            sponsorshipId: id,
            currentStake: stake,
            availableToUnstake: stake - minStakePerSponsorship,
            isStakeable: stakeableSponsorships.has(id)
        }))
        .sort((a, b) => {
            // Prefer sponsorships with more available stake
            if (b.availableToUnstake > a.availableToUnstake) return 1;
            if (a.availableToUnstake > b.availableToUnstake) return -1;
            return 0;
        });
    
    if (unstakeableSponsorships.length === 0) {
        return null;
    }
    
    // Try to find a single sponsorship that can cover the queue
    for (const sp of unstakeableSponsorships) {
        if (sp.availableToUnstake >= amountNeeded) {
            // This sponsorship can cover the queue payment
            // Add a small buffer (1%) to account for any timing differences
            const amountWithBuffer = amountNeeded + (amountNeeded / BigInt(100));
            const unstakeAmount = amountWithBuffer > sp.availableToUnstake 
                ? sp.availableToUnstake 
                : amountWithBuffer;
            
            const targetStake = sp.currentStake - unstakeAmount;
            
            return {
                type: 'unstake',
                sponsorshipId: sp.sponsorshipId,
                amount: unstakeAmount,
                targetStake: targetStake,
                currentStake: sp.currentStake,
                isQueuePayment: true
            };
        }
    }
    
    // No single sponsorship can cover it - use the one with most available
    const best = unstakeableSponsorships[0];
    const targetStake = minStakePerSponsorship;
    
    return {
        type: 'unstake',
        sponsorshipId: best.sponsorshipId,
        amount: best.availableToUnstake,
        targetStake: targetStake,
        currentStake: best.currentStake,
        isQueuePayment: true,
        partialPayment: true
    };
}

// Constants for retry logic
const MAX_RETRY_ATTEMPTS = 5;
const RETRY_DELAY_MS = 3000;
const RATE_LIMIT_DELAY_MS = 12000; // Wait 12s when rate limited (RPC says retry in 10s)

/**
 * Check if an error is a rate limit error from RPC
 * Uses the centralized isRateLimitError from services.js
 * @param {Error} error - The error to check
 * @returns {boolean} True if this is a rate limit error
 */
function isRateLimitError(error) {
    return isRateLimitErrorService(error);
}

/**
 * Check if an error is retryable (state changed, values outdated, or rate limited)
 * @param {Error} error - The error to check
 * @returns {boolean} True if the error suggests a retry might help
 */
function isRetryableError(error) {
    // Rate limit errors are always retryable
    if (isRateLimitError(error)) {
        return true;
    }
    
    const retryablePatterns = [
        'transfer amount exceeds balance',
        'insufficient balance',
        'queueIsEmpty',
        'FirstEmptyQueueThenStake',
        'execution reverted',
        'nonce too low',
        'replacement transaction underpriced',
        'already known',
        'ReduceStakeToZero',
        'CannotUnstakeBelowMinimum',
        'missing revert data',
        'server_error'
    ];
    
    const msg = error.message?.toLowerCase() || '';
    return retryablePatterns.some(pattern => msg.includes(pattern.toLowerCase()));
}

/**
 * Execute autostaker actions with retry and recalculation on failure
 * @param {Array} actions - Actions to execute
 * @param {string} operatorId - The operator contract address
 * @param {ethers.Signer} signer - Ethers signer
 * @param {Function} onProgress - Progress callback
 * @param {Object} config - Autostaker configuration (for recalculation)
 * @returns {Promise<Object>} Execution result
 */
/** V4 Operators have rebalanceStake */
async function isV4Operator(operatorContract) {
    try {
        return (await readWithFallback(() => operatorContract.version())).gte(OPERATOR_V4);
    } catch {
        return false;
    }
}

/**
 * The reductions and stakes of one rebalanceStake, checked as the one-by-one path does: a locked stake skips its
 * reduction, a partial one is kept at the sponsorship's minimum, and one with nothing to reduce is skipped
 */
async function prepareRebalance(unstakeActions, stakeActions, operatorContract, operatorId, provider) {
    const reduce = [];
    const skipped = [];
    for (const action of unstakeActions) {
        const sponsorship = new ethers.Contract(action.sponsorshipId, SPONSORSHIP_ABI, provider);
        const lockedStake = await readWithFallback(() => sponsorship.lockedStakeWei(operatorId));
        if (lockedStake.gt(0)) {
            skipped.push({ action, error: `Cannot unstake - ${ethers.utils.formatEther(lockedStake)} DATA is locked due to lock period` });
            continue;
        }
        let target = ethers.BigNumber.from('0x' + action.targetStake.toString(16));
        if (!target.isZero()) {
            // 0 leaves the sponsorship (unstake); a partial reduction keeps at least the minimum
            const [current, minimum] = await Promise.all([
                readWithFallback(() => operatorContract.stakedInto(action.sponsorshipId)),
                readWithFallback(() => sponsorship.minimumStakeOf(operatorId))
            ]);
            if (target.lt(minimum)) target = minimum;
            if (target.gte(current)) {
                skipped.push({ action, error: 'Cannot reduce stake - target is not less than current stake or at minimum' });
                continue;
            }
        }
        reduce.push({ action, target });
    }
    const stake = stakeActions.map(action => ({ action, amount: ethers.BigNumber.from('0x' + action.amount.toString(16)) }));
    return { reduce, stake, skipped };
}

// Like the official plugin: the rebalanceStake is retried 5 times, 5 s apart, before it is sent
const REBALANCE_RETRY_COUNT = 5;
const REBALANCE_RETRY_DELAY_MS = 5000;

/**
 * V4 Operators: all the reductions and stakes in one atomic rebalanceStake transaction (stake moves at once, the
 * undelegation queue is paid from the freed DATA). If any of them can't be done (e.g. a full sponsorship), nothing
 * is sent and no stake is touched; the next cycle recalculates from the chain.
 * @returns {Promise<Object|null>} the execution result, or null when there are fewer than 2 actions to batch
 */
async function executeRebalance(unstakeActions, stakeActions, operatorContract, operatorId, signer, gasSettings, onProgress) {
    const { reduce, stake, skipped } = await prepareRebalance(unstakeActions, stakeActions, operatorContract, operatorId, signer.provider);
    if (reduce.length + stake.length < 2) return null;
    const args = [
        reduce.map(r => r.action.sponsorshipId), reduce.map(r => r.target),
        stake.map(s => s.action.sponsorshipId), stake.map(s => s.amount)
    ];
    const actions = [...reduce, ...stake].map(item => item.action);
    const fail = (error) => ({ success: false, results: { successful: [], failed: [...actions.map(action => ({ action, error })), ...skipped] } });
    let gasLimit;
    for (let attempt = 1; !gasLimit; attempt++) {
        try {
            gasLimit = (await operatorContract.estimateGas.rebalanceStake(...args)).mul(13).div(10);
        } catch (e) {
            // A contract error (SponsorshipFull...) doesn't change on a retry
            const contractError = contractErrorMessage(e);
            console.warn(`[Autostaker] rebalanceStake would fail (attempt ${attempt}/${REBALANCE_RETRY_COUNT}):`, getFriendlyErrorMessage(e));
            if (contractError || attempt >= REBALANCE_RETRY_COUNT) return fail(contractError || getFriendlyErrorMessage(e));
            await new Promise(resolve => setTimeout(resolve, REBALANCE_RETRY_DELAY_MS));
        }
    }
    if (onProgress) {
        const moved = stake.reduce((sum, s) => sum + BigInt(s.amount.toString()), 0n);
        onProgress({ current: 1, total: 1, action: { type: 'rebalance', sponsorshipId: operatorId, amount: moved, count: actions.length } });
    }
    try {
        console.log(`[Autostaker] Calling rebalanceStake: ${reduce.length} reduction(s), ${stake.length} stake(s)`);
        const tx = await operatorContract.rebalanceStake(...args, { ...gasSettings, gasLimit });
        const receipt = await tx.wait();
        return {
            success: skipped.length === 0,
            results: { successful: actions.map(action => ({ action, txHash: receipt.transactionHash })), failed: skipped }
        };
    } catch (e) {
        // Not retried: a sent transaction may have gone through. The next cycle recalculates from the chain.
        console.error('[Autostaker] rebalanceStake failed:', e);
        return fail(getFriendlyErrorMessage(e));
    }
}

export async function executeActions(actions, operatorId, signer, onProgress, config = null) {
    if (!actions || actions.length === 0) {
        return { success: true, message: 'No actions to execute' };
    }
    
    const operatorContract = new ethers.Contract(operatorId, OPERATOR_CONTRACT_ABI, signer);
    
    // Get current gas prices from the network
    // Use fixed gasLimit like official plugin to avoid gas estimation failures
    // when stake actions are calculated before unstakes complete
    const ACTION_GAS_LIMIT = 800000;
    
    let gasSettings;
    try {
        const feeData = await signer.provider.getFeeData();
        // Use higher gas prices for Polygon to avoid "gas price below minimum" errors
        const minPriorityFee = ethers.utils.parseUnits('30', 'gwei');
        const minMaxFee = ethers.utils.parseUnits('100', 'gwei');
        
        gasSettings = {
            gasLimit: ACTION_GAS_LIMIT,
            maxPriorityFeePerGas: feeData.maxPriorityFeePerGas?.gt(minPriorityFee) 
                ? feeData.maxPriorityFeePerGas 
                : minPriorityFee,
            maxFeePerGas: feeData.maxFeePerGas?.gt(minMaxFee)
                ? feeData.maxFeePerGas
                : minMaxFee
        };
    } catch (e) {
        console.warn('Failed to get fee data, using defaults:', e.message);
        gasSettings = {
            gasLimit: ACTION_GAS_LIMIT,
            maxPriorityFeePerGas: ethers.utils.parseUnits('50', 'gwei'),
            maxFeePerGas: ethers.utils.parseUnits('200', 'gwei')
        };
    }
    
    // Separate unstake and stake actions (unstake first to free up funds)
    const unstakeActions = actions.filter(a => a.type === 'unstake');
    const stakeActions = actions.filter(a => a.type === 'stake');
    const orderedActions = [...unstakeActions, ...stakeActions];
    
    const results = {
        successful: [],
        failed: []
    };
    
    // First, try to pay out any pending undelegation queue if there are funds available
    // This helps avoid issues where unstake funds go to queue instead of being available
    try {
        const queueIsEmpty = await readWithFallback(() => operatorContract.queueIsEmpty());
        if (!queueIsEmpty) {
            console.log('[Autostaker] Undelegation queue not empty, checking if we can pay it out...');
            
            // Try to pay out the queue first (0 = pay all possible with available funds)
            try {
                const payoutTx = await operatorContract.payOutQueue(0, gasSettings);
                await payoutTx.wait();
                console.log('[Autostaker] Queue payout completed before starting actions');
            } catch (payoutError) {
                // This is expected if there are no free funds - continue with actions
                console.log('[Autostaker] Could not pay queue (likely no free funds):', payoutError.message?.substring(0, 100));
            }
        }
    } catch (e) {
        console.warn('[Autostaker] Failed to check/pay queue status:', e.message);
    }
    
    // Check again if we have stake actions and queue status after potential payout
    if (stakeActions.length > 0) {
        try {
            const queueIsEmpty = await readWithFallback(() => operatorContract.queueIsEmpty());
            if (!queueIsEmpty) {
                // Queue still not empty - need more funds
                // Filter out stake actions, only do unstakes first
                console.warn('[Autostaker] Queue still not empty, will skip stake actions until queue is cleared');
                
                // If we have unstake actions, proceed with them (they will help pay the queue)
                if (unstakeActions.length > 0) {
                    console.log('[Autostaker] Proceeding with unstake actions to help pay queue');
                } else {
                    // No unstakes and queue not empty - can't do stakes
                    return {
                        success: false,
                        message: 'Undelegation queue not empty - need to unstake first to free funds',
                        results: {
                            successful: [],
                            failed: stakeActions.map(a => ({
                                action: a,
                                error: 'Undelegation queue not empty - unstake first to free funds for queue payout'
                            }))
                        }
                    };
                }
            }
        } catch (e) {
            console.warn('[Autostaker] Failed to check queue status:', e.message);
        }
    }
    
    // V4 Operators: one atomic rebalanceStake transaction for all of them, never one by one
    if (orderedActions.length > 1 && await isV4Operator(operatorContract)) {
        const rebalanced = await executeRebalance(unstakeActions, stakeActions, operatorContract, operatorId, signer, gasSettings, onProgress);
        if (rebalanced) return rebalanced;
    }
    
    // Track freed funds from unstakes to validate stakes
    let recalculationAttempts = 0;
    let currentActions = [...orderedActions];
    let actionIndex = 0;
    let displayedActionNumber = 0; // Simple counter for progress display
    const originalTotalActions = orderedActions.length; // Track original count for progress
    
    while (actionIndex < currentActions.length) {
        const action = currentActions[actionIndex];
        displayedActionNumber++;
        
        // Simple progress: current action number / original total
        if (onProgress) {
            onProgress({
                current: displayedActionNumber,
                total: originalTotalActions,
                action,
                isRetry: recalculationAttempts > 0
            });
        }
        
        try {
            let tx;
            if (action.type === 'stake') {
                // For stakes, first verify the operator has enough free balance
                const valueWithoutEarnings = await readWithFallback(() => operatorContract.valueWithoutEarnings());
                const totalStakedIntoSponsorships = await readWithFallback(() => operatorContract.totalStakedIntoSponsorshipsWei());
                const actualFreeBalance = valueWithoutEarnings.sub(totalStakedIntoSponsorships);
                
                const amountHex = '0x' + action.amount.toString(16);
                const amountBN = ethers.BigNumber.from(amountHex);
                
                // Check if we have enough free balance
                if (amountBN.gt(actualFreeBalance)) {
                    console.warn(`[Autostaker] Insufficient free balance! Need ${ethers.utils.formatEther(amountBN)} DATA but only have ${ethers.utils.formatEther(actualFreeBalance)} DATA`);
                    
                    // If there's any free balance, stake what we can
                    if (actualFreeBalance.gt(0)) {
                        console.log(`[Autostaker] Staking available amount: ${ethers.utils.formatEther(actualFreeBalance)} DATA instead`);
                        tx = await operatorContract.stake(action.sponsorshipId, actualFreeBalance, gasSettings);
                    } else {
                        results.failed.push({
                            action,
                            error: `Insufficient free balance: need ${ethers.utils.formatEther(amountBN)} DATA but have ${ethers.utils.formatEther(actualFreeBalance)} DATA`
                        });
                        actionIndex++;
                        continue;
                    }
                } else {
                    console.log(`[Autostaker] Staking ${ethers.utils.formatEther(amountBN)} DATA to ${action.sponsorshipId}`);
                    tx = await operatorContract.stake(action.sponsorshipId, amountBN, gasSettings);
                }
            } else {
                // For unstakes
                // Convert BigInt to ethers.BigNumber via hex (avoids precision issues)
                const targetStakeHex = '0x' + action.targetStake.toString(16);
                let targetStakeBN = ethers.BigNumber.from(targetStakeHex);
                
                // Verify targetStake is valid before calling contract
                const currentStakeOnChain = await readWithFallback(() => operatorContract.stakedInto(action.sponsorshipId));
                
                // Check minimumStakeOf on the Sponsorship contract
                const sponsorshipContract = new ethers.Contract(action.sponsorshipId, SPONSORSHIP_ABI, signer.provider);
                const minimumStakeOf = await readWithFallback(() => sponsorshipContract.minimumStakeOf(operatorId));
                const lockedStake = await readWithFallback(() => sponsorshipContract.lockedStakeWei(operatorId));
                
                // Check if there's locked stake preventing full unstake
                if (lockedStake.gt(0)) {
                    console.warn(`[Autostaker] Cannot unstake: ${lockedStake.toString()} wei is locked (lock period active)`);
                    results.failed.push({
                        action,
                        error: `Cannot unstake - ${ethers.utils.formatEther(lockedStake)} DATA is locked due to lock period`
                    });
                    actionIndex++;
                    continue;
                }
                
                // If target is 0, use unstake() for complete withdrawal (ignores minimumStakeOf)
                if (action.targetStake === BigInt(0)) {
                    console.log(`[Autostaker] Calling unstake(${action.sponsorshipId}) for complete withdrawal`);
                    tx = await operatorContract.unstake(action.sponsorshipId, gasSettings);
                } else {
                    // For partial unstake, respect the minimum
                    if (targetStakeBN.lt(minimumStakeOf)) {
                        console.warn(`[Autostaker] Target ${targetStakeBN.toString()} is below minimum ${minimumStakeOf.toString()}, adjusting to minimum`);
                        targetStakeBN = minimumStakeOf;
                    }
                    
                    // Check if there's actually anything to reduce
                    if (targetStakeBN.gte(currentStakeOnChain)) {
                        console.warn(`[Autostaker] Skipping unstake: targetStake (${targetStakeBN.toString()}) >= onChainStake (${currentStakeOnChain.toString()})`);
                        results.failed.push({
                            action,
                            error: 'Cannot reduce stake - target is not less than current stake or at minimum'
                        });
                        actionIndex++;
                        continue;
                    }
                    
                    console.log(`[Autostaker] Calling reduceStakeTo(${action.sponsorshipId}, ${targetStakeBN.toString()})`);
                    tx = await operatorContract.reduceStakeTo(action.sponsorshipId, targetStakeBN, gasSettings);
                }
            }
            
            const receipt = await tx.wait();
            results.successful.push({
                action,
                txHash: receipt.transactionHash
            });
            
            // If this was a queue payment unstake, try to pay out the queue now
            if (action.isQueuePayment) {
                console.log('[Autostaker] Queue payment unstake successful, attempting to pay out queue...');
                try {
                    // Small delay to ensure state is updated
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    
                    const payoutTx = await operatorContract.payOutQueue(0, gasSettings);
                    const payoutReceipt = await payoutTx.wait();
                    console.log(`[Autostaker] Queue payout successful! Tx: ${payoutReceipt.transactionHash}`);
                    
                    results.successful.push({
                        action: { type: 'queuePayout', description: 'Pay undelegation queue' },
                        txHash: payoutReceipt.transactionHash
                    });
                } catch (payoutError) {
                    console.warn('[Autostaker] Failed to pay out queue after unstake:', payoutError.message);
                    // Not critical - queue will be paid in next cycle
                }
            }
            
            // Move to next action
            actionIndex++;
            recalculationAttempts = 0; // Reset retry counter on success
            
            // Small delay between transactions to avoid nonce issues
            await new Promise(resolve => setTimeout(resolve, 2000));
            
        } catch (e) {
            console.error(`Failed to execute ${action.type} action:`, e);
            
            // Check if the transaction was already submitted (has hash)
            // If so, it either succeeded or failed on-chain - recalculate to find out
            const txWasSubmitted = e?.transactionHash || e?.transaction?.hash || e?.receipt;
            if (txWasSubmitted) {
                console.log(`[Autostaker] Transaction was submitted but may have failed on-chain. Recalculating...`);
            }
            
            // Check if this is a retryable error and we have config for recalculation
            if (config && isRetryableError(e) && recalculationAttempts < MAX_RETRY_ATTEMPTS) {
                recalculationAttempts++;
                
                // Use longer delay for rate limit errors, or if tx was submitted (wait for chain state)
                const isRateLimit = isRateLimitError(e);
                const delayMs = txWasSubmitted ? RETRY_DELAY_MS : (isRateLimit ? RATE_LIMIT_DELAY_MS : RETRY_DELAY_MS);
                
                console.log(`[Autostaker] ${txWasSubmitted ? 'Tx submitted,' : (isRateLimit ? 'Rate limit' : 'Retryable')} error detected, waiting ${delayMs/1000}s before retry (attempt ${recalculationAttempts}/${MAX_RETRY_ATTEMPTS})...`);
                
                if (onProgress) {
                    onProgress({
                        current: actionIndex + 1,
                        total: currentActions.length,
                        action,
                        isRecalculating: true,
                        retryAttempt: recalculationAttempts,
                        isRateLimited: isRateLimit
                    });
                }
                
                // Wait for chain state to settle (longer for rate limits)
                await new Promise(resolve => setTimeout(resolve, delayMs));
                
                // If rate limited, try switching to a fallback RPC
                if (isRateLimit) {
                    try {
                        switchToFallbackRpc();
                        console.log(`[Autostaker] Switched to fallback RPC: ${getCurrentRpcUrl()}`);
                        // Update the signer's provider connection for subsequent calls
                        // Note: The signer itself can't be changed, but read operations will use fallback
                    } catch (switchError) {
                        console.warn('[Autostaker] Failed to switch RPC:', switchError.message);
                    }
                }
                
                try {
                    // Recalculate actions based on current on-chain state
                    const newAnalysis = await analyzeAndCalculateActions(operatorId, config, operatorContract);
                    
                    if (newAnalysis.actions && newAnalysis.actions.length > 0) {
                        console.log(`[Autostaker] Recalculated ${newAnalysis.actions.length} new actions`);
                        
                        // Filter out actions that were already successful (in results.successful)
                        const successfulSponsorships = new Set(results.successful.map(r => r.action.sponsorshipId));
                        const remainingActions = newAnalysis.actions.filter(a => !successfulSponsorships.has(a.sponsorshipId));
                        
                        // Count actions that completed on-chain but weren't in results.successful yet
                        // These are actions where tx was sent but tx.wait() failed with rate limit
                        const completedOnChain = currentActions.filter(a => {
                            // Action was in our list but is NOT in the new recalculated actions
                            // AND is not already counted as successful
                            const stillNeeded = newAnalysis.actions.some(na => na.sponsorshipId === a.sponsorshipId);
                            const alreadyCounted = successfulSponsorships.has(a.sponsorshipId);
                            return !stillNeeded && !alreadyCounted;
                        });
                        
                        // Add these to successful results (tx hash unknown due to rate limit during wait)
                        for (const completedAction of completedOnChain) {
                            results.successful.push({
                                action: completedAction,
                                txHash: 'confirmed-via-recalculation'
                            });
                            console.log(`[Autostaker] Action ${completedAction.type} for ${completedAction.sponsorshipId.substring(0,10)}... confirmed via recalculation`);
                        }
                        
                        if (remainingActions.length === 0) {
                            // All actions were actually completed on-chain
                            console.log('[Autostaker] All actions already completed on-chain, finishing');
                            break;
                        }
                        
                        // Replace current actions with recalculated ones
                        const unstakes = remainingActions.filter(a => a.type === 'unstake');
                        const stakes = remainingActions.filter(a => a.type === 'stake');
                        currentActions = [...unstakes, ...stakes];
                        actionIndex = 0; // Restart from the beginning of new actions
                        recalculationAttempts = 0; // Reset retry counter for new action set
                        
                        console.log(`[Autostaker] Continuing with ${currentActions.length} recalculated actions`);
                        continue; // Continue with the new actions
                    } else {
                        // No actions needed - state is already balanced
                        // Count any remaining actions as successful (completed on-chain)
                        const successfulSponsorships = new Set(results.successful.map(r => r.action.sponsorshipId));
                        for (const pendingAction of currentActions) {
                            if (!successfulSponsorships.has(pendingAction.sponsorshipId)) {
                                results.successful.push({
                                    action: pendingAction,
                                    txHash: 'confirmed-via-recalculation'
                                });
                            }
                        }
                        console.log('[Autostaker] Recalculation shows state is balanced, finishing successfully');
                        break; // Exit loop - job is done
                    }
                } catch (recalcError) {
                    console.error('[Autostaker] Failed to recalculate actions:', recalcError);
                }
            }
            
            // Extract more user-friendly error message
            let errorMsg = e.message;
            if (e.message.includes('transfer amount exceeds balance')) {
                errorMsg = 'Insufficient balance - operator does not have enough free DATA';
            } else if (e.message.includes('gas price below minimum')) {
                errorMsg = 'Gas price too low - network congested';
            } else if (e.message.includes('FirstEmptyQueueThenStake') || e.message.includes('queueIsEmpty')) {
                errorMsg = 'Undelegation queue not empty - cannot stake until queue is paid out';
            }
            
            results.failed.push({
                action,
                error: errorMsg,
                retriesAttempted: recalculationAttempts
            });
            
            // Move to next action even on failure
            actionIndex++;
            recalculationAttempts = 0; // Reset for next action
        }
    }
    
    return {
        success: results.failed.length === 0,
        results
    };
}

/**
 * Format action for display
 * @param {Object} action - Action object
 * @param {Map} sponsorshipInfo - Map of sponsorship info
 * @returns {string} Formatted action string
 */
export function formatActionForDisplay(action, sponsorshipInfo) {
    const amountData = convertWeiToData(action.amount.toString());
    const info = sponsorshipInfo?.get(action.sponsorshipId);
    const streamId = info?.streamId || action.sponsorshipId;
    const truncatedId = streamId.length > 40 ? streamId.substring(0, 37) + '...' : streamId;
    
    if (action.type === 'stake') {
        return `Stake ${formatBigNumber(amountData)} DATA → ${truncatedId}`;
    } else {
        return `Unstake ${formatBigNumber(amountData)} DATA ← ${truncatedId}`;
    }
}

/**
 * Check if it's time to auto-collect based on config
 * @param {Object} config - Autostaker configuration
 * @returns {boolean} True if auto-collect should run
 */
export function shouldAutoCollect(config) {
    if (!config.autoCollectEnabled) {
        return false;
    }
    
    if (!config.lastCollectTime) {
        // Never collected before, do it now
        return true;
    }
    
    const lastCollect = new Date(config.lastCollectTime);
    const now = new Date();
    const hoursSinceLastCollect = (now - lastCollect) / (1000 * 60 * 60);
    
    return hoursSinceLastCollect >= config.autoCollectIntervalHours;
}

/**
 * Execute auto-collect earnings from all sponsorships
 * @param {string} operatorId - The operator contract address
 * @param {ethers.Signer} signer - Ethers signer
 * @param {Function} onLog - Callback to log messages (type, message)
 * @returns {Promise<Object>} Result with success status and txHash
 */
export async function executeAutoCollect(operatorId, signer, onLog) {
    onLog?.('info', '💰 Starting auto-collect earnings...');
    
    try {
        // Fetch current stakes to get sponsorship IDs
        const currentStakes = await fetchCurrentStakes(operatorId);
        
        if (currentStakes.size === 0) {
            onLog?.('info', 'No active stakes to collect from');
            return { success: true, message: 'No stakes to collect', skipped: true };
        }
        
        const sponsorshipIds = [...currentStakes.keys()];
        onLog?.('info', `Collecting from ${sponsorshipIds.length} sponsorship(s)...`);
        
        // Get gas settings
        let gasSettings = {};
        try {
            const feeData = await signer.provider.getFeeData();
            const minPriorityFee = ethers.utils.parseUnits('30', 'gwei');
            const minMaxFee = ethers.utils.parseUnits('100', 'gwei');
            
            gasSettings = {
                maxPriorityFeePerGas: feeData.maxPriorityFeePerGas?.gt(minPriorityFee) 
                    ? feeData.maxPriorityFeePerGas 
                    : minPriorityFee,
                maxFeePerGas: feeData.maxFeePerGas?.gt(minMaxFee)
                    ? feeData.maxFeePerGas
                    : minMaxFee
            };
        } catch (e) {
            console.warn('Failed to get fee data for auto-collect, using defaults:', e.message);
            gasSettings = {
                maxPriorityFeePerGas: ethers.utils.parseUnits('50', 'gwei'),
                maxFeePerGas: ethers.utils.parseUnits('200', 'gwei')
            };
        }
        
        // Execute withdraw
        const operatorContract = new ethers.Contract(operatorId, OPERATOR_CONTRACT_ABI, signer);
        const tx = await operatorContract.withdrawEarningsFromSponsorships(sponsorshipIds, gasSettings);
        
        onLog?.('info', 'Transaction submitted, waiting for confirmation...');
        const receipt = await tx.wait();
        
        onLog?.('success', `✅ Collected earnings! Tx: ${receipt.transactionHash.substring(0, 10)}...`);
        
        return {
            success: true,
            txHash: receipt.transactionHash,
            sponsorshipsCount: sponsorshipIds.length
        };
        
    } catch (e) {
        console.error('[Auto-Collect] Error:', e);
        
        let errorMsg = e.message;
        if (e.message.includes('gas price below minimum')) {
            errorMsg = 'Gas price too low - network congested';
        }
        
        onLog?.('error', `❌ Auto-collect failed: ${errorMsg.substring(0, 100)}`);
        
        return {
            success: false,
            error: errorMsg
        };
    }
}

/**
 * Get time until next auto-collect
 * @param {Object} config - Autostaker configuration
 * @returns {Object} Object with hours, minutes, and formatted string
 */
export function getTimeUntilNextCollect(config) {
    if (!config.autoCollectEnabled) {
        return { hours: 0, minutes: 0, formatted: 'Disabled' };
    }
    
    if (!config.lastCollectTime) {
        return { hours: 0, minutes: 0, formatted: 'Next cycle' };
    }
    
    const lastCollect = new Date(config.lastCollectTime);
    const nextCollect = new Date(lastCollect.getTime() + config.autoCollectIntervalHours * 60 * 60 * 1000);
    const now = new Date();
    
    const msUntil = nextCollect - now;
    
    if (msUntil <= 0) {
        return { hours: 0, minutes: 0, formatted: 'Next cycle' };
    }
    
    const hours = Math.floor(msUntil / (1000 * 60 * 60));
    const minutes = Math.floor((msUntil % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
        return { hours, minutes, formatted: `${hours}h ${minutes}m` };
    } else {
        return { hours, minutes, formatted: `${minutes}m` };
    }
}

